import React, { useCallback, useEffect, useState } from 'react';
import api from './api';
import './styles/index.css';
import SponsorsSection from './SponsorsSection';

// Validaciones de usuario y contraseña (copiadas de la app)
const validateUsername = (username) => {
  const errors = [];
  
  if (username.length < 3) {
    errors.push('Mínimo 3 caracteres');
  }
  if (username.length > 15) {
    errors.push('Máximo 15 caracteres');
  }
  if (!/^[a-zA-Z0-9 ]+$/.test(username)) {
    errors.push('Solo letras, números y espacios');
  }
  if (/^\s|\s$/.test(username)) {
    errors.push('No puede empezar o terminar con espacio');
  }
  if (/\s{2,}/.test(username)) {
    errors.push('No puede tener espacios consecutivos');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Política para torneo: mínimo 4 caracteres (puede ser solo números para nuevos jugadores)
const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 4) {
    errors.push('Mínimo 4 caracteres');
  }
  if (password.length > 30) {
    errors.push('Máximo 30 caracteres');
  }
  
  // Solo evitar contraseñas extremadamente débiles
  const veryWeakPasswords = ['1234', '0000', '1111', '123456', '000000', 'password1', 'qwerty1', 'abcdef1'];
  if (veryWeakPasswords.includes(password.toLowerCase())) {
    errors.push('Contraseña demasiado común, elige otra');
  }
  
  let strength = 0;
  if (password.length >= 4) strength++;
  if (password.length >= 6 && /[0-9]/.test(password)) strength++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;
  
  return {
    isValid: errors.length === 0,
    errors,
    strength
  };
};

const getPasswordStrengthText = (strength) => {
  if (strength <= 1) return 'Débil';
  if (strength === 2) return 'Media';
  if (strength === 3) return 'Fuerte';
  return 'Muy fuerte';
};

const getPasswordStrengthColor = (strength) => {
  if (strength <= 1) return '#ff4444';
  if (strength === 2) return '#FFA726';
  if (strength === 3) return '#4CAF50';
  return '#00C6CF';
};

const Login = ({ onLoginSuccess, sessionNotice = '' }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordHelp, setShowPasswordHelp] = useState(false);
  const [showUsernameHelp, setShowUsernameHelp] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // IDs de videos de YouTube
  const youtubeVideoIds = [
    'xkP6XoxwapY', // lukkeliter
    '_teOdMl_3sk', // jesus180
    '9pe6GxOu-4Q', // fondojesuscierre
    's9wG1TUZuHE'  // philtaylor
  ];

  const usernameValidation = validateUsername(username);
  const passwordValidation = validatePassword(password);

  // Cambiar al siguiente video cuando el actual termine
  const onPlayerStateChange = useCallback((event) => {
    // 0 = video terminado
    if (event.data === 0) {
      console.log('Video terminado, cambiando al siguiente...');
      setCurrentVideoIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % youtubeVideoIds.length;
        console.log(`Índice actual: ${prevIndex}, Siguiente: ${nextIndex}`);
        return nextIndex;
      });
    }
  }, [youtubeVideoIds.length]);

  const initializePlayer = useCallback(() => {
    if (window.player) {
      window.player.destroy();
    }

    window.player = new window.YT.Player('youtube-background', {
      events: {
        onStateChange: onPlayerStateChange,
        onReady: (event) => {
          event.target.playVideo();
        },
      },
    });
  }, [onPlayerStateChange]);

  // Cargar la API de YouTube y configurar el reproductor
  useEffect(() => {
    // Cargar script de YouTube API solo una vez
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Configurar el callback cuando la API esté lista
    window.onYouTubeIframeAPIReady = () => {
      initializePlayer();
    };

    // Si la API ya está cargada, inicializar inmediatamente
    if (window.YT && window.YT.Player) {
      initializePlayer();
    }

    return () => {
      if (window.player) {
        window.player.destroy();
        window.player = null;
      }
    };
  }, [currentVideoIndex, initializePlayer]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    const finalUsername = username.trim();
    
    if (!finalUsername || !nombre.trim() || !password || !confirmPassword) {
      setError('Por favor, completa todos los campos');
      return;
    }

    const finalValidation = validateUsername(finalUsername);
    if (!finalValidation.isValid) {
      setError('Nombre de usuario inválido: ' + finalValidation.errors.join(', '));
      return;
    }

    if (!passwordValidation.isValid) {
      setError('Contraseña no segura: ' + passwordValidation.errors.join(', '));
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      await api.register({
        username: finalUsername,
        nombre: nombre.trim(),
        password,
        confirmPassword,
      });
      onLoginSuccess();
    } catch (err) {
      setError(err.message || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.login(username.trim(), password);
      onLoginSuccess();
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setUsername('');
    setNombre('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordHelp(false);
    setShowUsernameHelp(false);
  };

  return (
    <div className="dart-league-login">
      {/* Video de fondo de YouTube - solo en pantallas grandes */}
      <iframe
        id="youtube-background"
        key={currentVideoIndex}
        className="login-background-video"
        src={`https://www.youtube.com/embed/${youtubeVideoIds[currentVideoIndex]}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&enablejsapi=1`}
        title="Background Video"
        frameBorder="0"
        allow="autoplay; encrypted-media"
        style={{ pointerEvents: 'none' }}
      />
      
      <div className="login-container">
        <div className="login-header">
          <img src="/Logo26.png" alt="501 Dart League" className="login-logo" />
          <h1>501 Dart League</h1>
          <p>{isLogin ? 'Inicia sesión para ver tus estadísticas' : 'Crea tu cuenta para comenzar'}</p>
        </div>

        {isLogin && sessionNotice && (
          <div className="info-message">
            <p><strong>Sesión caducada:</strong> {sessionNotice}</p>
          </div>
        )}

        {!isLogin && (
          <div className="info-message">
            <p>ℹ️ <strong>Importante:</strong></p>
            <ul>
              <li>Si tu usuario aparece en uso, ponte en contacto con tu operador.</li>
              <li>Si crees que ya tienes una cuenta, pídela a tu operador.</li>
              <li>Si no tienes noticia de que tu grupo de liga ya se haya registrado, ¡regístrate!</li>
            </ul>
          </div>
        )}

        <form onSubmit={isLogin ? handleLogin : handleRegister} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Nombre de usuario</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ' '))}
              placeholder="Tu nombre de usuario"
              required
              autoFocus
              disabled={loading}
              maxLength={15}
            />
            {!isLogin && username.length > 0 && (
              <div className="validation-info">
                <div className="validation-header">
                  <span className="char-count">{username.length}/15 caracteres</span>
                  <button
                    type="button"
                    className="help-btn"
                    onClick={() => setShowUsernameHelp(!showUsernameHelp)}
                  >
                    ?
                  </button>
                </div>
                {(showUsernameHelp || usernameValidation.errors.length > 0) && (
                  <div className={`validation-errors ${usernameValidation.isValid ? 'valid' : 'invalid'}`}>
                    <p className="validation-title">
                      {usernameValidation.isValid ? '✓ Nombre de usuario válido' : 'Requisitos del nombre de usuario:'}
                    </p>
                    {!usernameValidation.isValid && usernameValidation.errors.map((error, i) => (
                      <p key={i} className="validation-error">• {error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="nombre">Nombre completo</label>
              <input
                id="nombre"
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Tu nombre completo"
                required
                disabled={loading}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="password-input">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Tu contraseña"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {!isLogin && password.length > 0 && (
              <div className="validation-info">
                <div className="validation-header">
                  <span className="password-strength">
                    Seguridad: <span style={{ color: getPasswordStrengthColor(passwordValidation.strength) }}>
                      {getPasswordStrengthText(passwordValidation.strength)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="help-btn"
                    onClick={() => setShowPasswordHelp(!showPasswordHelp)}
                  >
                    ?
                  </button>
                </div>
                {(showPasswordHelp || passwordValidation.errors.length > 0) && (
                  <div className="validation-errors invalid">
                    <p className="validation-title">Requisitos de contraseña:</p>
                    {passwordValidation.errors.map((error, i) => (
                      <p key={i} className="validation-error">• {error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar contraseña</label>
              <div className="password-input">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirma tu contraseña"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-button"
            disabled={loading || !username || !password || (!isLogin && (!nombre || !confirmPassword))}
          >
            {loading ? '⏳ Procesando...' : (isLogin ? '🎯 Entrar' : '✨ Registrarse')}
          </button>
        </form>

        <button 
          className="toggle-mode-button"
          onClick={toggleMode}
          disabled={loading}
        >
          {isLogin ? '¿No tienes cuenta? Regístrate aquí' : '¿Ya tienes cuenta? Inicia sesión'}
        </button>

        <div className="login-footer">
          <p>
            También disponible en <a href="https://play.google.com/store/apps/details?id=com.dartsgame.esp501" target="_blank" rel="noopener noreferrer">Google Play</a>
          </p>
        </div>
      </div>

      {/* Banner de patrocinadores - Visible en todas las pantallas */}
      <SponsorsSection />
    </div>
  );
};

export default Login;
